import { useState } from 'react';
import { Upload, X } from 'lucide-react';

interface ProductFormProps {
  onSubmit: (product: ProductFormData) => void;
}

export interface ProductFormData {
  title: string;
  category: string;
  subcategory: string;
  description: string;
  price: number;
  images: string[];
  inventory: number;
  condition: string;
  warranty: string;
  shipping: string;
  returns: string;
  keywords: string[];
}

const categories = [
  {
    name: 'Equipment',
    subcategories: ['Training', 'Competition', 'Accessories']
  },
  {
    name: 'Apparel',
    subcategories: ['Uniforms', 'Training Wear', 'Footwear']
  },
  {
    name: 'Services',
    subcategories: ['Coaching', 'Training Programs', 'Nutrition']
  }
];

const ProductForm = ({ onSubmit }: ProductFormProps) => {
  const [formData, setFormData] = useState<ProductFormData>({
    title: '',
    category: '',
    subcategory: '',
    description: '',
    price: 0,
    images: [],
    inventory: 1,
    condition: 'new',
    warranty: '',
    shipping: '',
    returns: '',
    keywords: []
  });

  const [keywordInput, setKeywordInput] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof ProductFormData, string>>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newImages = Array.from(files).map(file => URL.createObjectURL(file));
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages].slice(0, 5)
      }));
    }
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addKeyword = () => {
    if (keywordInput.trim() && formData.keywords.length < 8) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const removeKeyword = (index: number) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter((_, i) => i !== index)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ProductFormData, string>> = {};

    if (!formData.title) newErrors.title = 'Title is required';
    if (formData.title.length > 60) newErrors.title = 'Title must be less than 60 characters';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.subcategory) newErrors.subcategory = 'Subcategory is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (formData.price <= 0) newErrors.price = 'Price must be greater than 0';
    if (formData.images.length === 0) newErrors.images = 'At least one image is required';
    if (formData.inventory < 1) newErrors.inventory = 'Inventory must be at least 1';
    if (!formData.shipping) newErrors.shipping = 'Shipping information is required';
    if (!formData.returns) newErrors.returns = 'Return policy is required';
    if (formData.keywords.length < 5) newErrors.keywords = 'At least 5 keywords are required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Basic Information</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Title
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            maxLength={60}
            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 focus:ring-green-500"
            placeholder="Enter product title"
          />
          {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
          <p className="mt-1 text-sm text-gray-500">{formData.title.length}/60 characters</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Category
              <span className="text-red-500">*</span>
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 focus:ring-green-500"
            >
              <option value="">Select category</option>
              {categories.map(cat => (
                <option key={cat.name} value={cat.name}>{cat.name}</option>
              ))}
            </select>
            {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Subcategory
              <span className="text-red-500">*</span>
            </label>
            <select
              name="subcategory"
              value={formData.subcategory}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 focus:ring-green-500"
              disabled={!formData.category}
            >
              <option value="">Select subcategory</option>
              {formData.category && categories
                .find(cat => cat.name === formData.category)
                ?.subcategories.map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))
              }
            </select>
            {errors.subcategory && <p className="mt-1 text-sm text-red-500">{errors.subcategory}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Description
            <span className="text-red-500">*</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 focus:ring-green-500"
            placeholder="Describe your product in detail"
          />
          {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
        </div>
      </div>

      {/* Pricing and Inventory */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Pricing and Inventory</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Price (USD)
              <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 focus:ring-green-500"
            />
            {errors.price && <p className="mt-1 text-sm text-red-500">{errors.price}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Inventory
              <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="inventory"
              value={formData.inventory}
              onChange={handleChange}
              min="1"
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 focus:ring-green-500"
            />
            {errors.inventory && <p className="mt-1 text-sm text-red-500">{errors.inventory}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Condition
            <span className="text-red-500">*</span>
          </label>
          <select
            name="condition"
            value={formData.condition}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 focus:ring-green-500"
          >
            <option value="new">New</option>
            <option value="like-new">Like New</option>
            <option value="good">Good</option>
            <option value="fair">Fair</option>
          </select>
        </div>
      </div>

      {/* Images */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Images</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Product Images
            <span className="text-red-500">*</span>
            <span className="text-sm text-gray-500 ml-2">(3-5 images, min 800x800px)</span>
          </label>
          
          <div className="mt-2 flex items-center">
            <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
              <Upload className="h-5 w-5 mr-2" />
              Upload Images
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>

          {errors.images && <p className="mt-1 text-sm text-red-500">{errors.images}</p>}

          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {formData.images.map((image, index) => (
              <div key={index} className="relative">
                <img
                  src={image}
                  alt={`Product ${index + 1}`}
                  className="h-32 w-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sales Terms */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Sales Terms</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Warranty Information
          </label>
          <textarea
            name="warranty"
            value={formData.warranty}
            onChange={handleChange}
            rows={2}
            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 focus:ring-green-500"
            placeholder="Describe warranty terms if applicable"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Shipping Information
            <span className="text-red-500">*</span>
          </label>
          <textarea
            name="shipping"
            value={formData.shipping}
            onChange={handleChange}
            rows={2}
            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 focus:ring-green-500"
            placeholder="Describe shipping options and costs"
          />
          {errors.shipping && <p className="mt-1 text-sm text-red-500">{errors.shipping}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Return Policy
            <span className="text-red-500">*</span>
          </label>
          <textarea
            name="returns"
            value={formData.returns}
            onChange={handleChange}
            rows={2}
            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 focus:ring-green-500"
            placeholder="Describe return and refund policy"
          />
          {errors.returns && <p className="mt-1 text-sm text-red-500">{errors.returns}</p>}
        </div>
      </div>

      {/* Keywords */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Keywords</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Search Keywords
            <span className="text-red-500">*</span>
            <span className="text-sm text-gray-500 ml-2">(5-8 keywords)</span>
          </label>
          
          <div className="mt-2 flex">
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
              className="flex-1 rounded-l-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 focus:ring-green-500"
              placeholder="Enter keyword and press Enter"
            />
            <button
              type="button"
              onClick={addKeyword}
              disabled={formData.keywords.length >= 8}
              className="px-4 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-500"
            >
              Add
            </button>
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            {formData.keywords.map((keyword, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
              >
                {keyword}
                <button
                  type="button"
                  onClick={() => removeKeyword(index)}
                  className="ml-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
          {errors.keywords && <p className="mt-1 text-sm text-red-500">{errors.keywords}</p>}
        </div>
      </div>

      {/* Submit Button */}
      <div className="pt-4">
        <button
          type="submit"
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          List Product
        </button>
      </div>
    </form>
  );
};

export default ProductForm;